import { Head, Link, router, usePage } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import { Plus, Eye, Check, Download } from 'lucide-react'
import DataTable, { Column } from '@/components/DataTable'
import DateRangeFilter, { DateRange, getDateLabel } from '@/components/DateRangeFilter'

interface Commission {
  id: number
  agent_id: number
  agent_name: string
  carrier_name: string
  policy_number: string
  commission_type: string
  gross_amount: number
  agent_amount: number
  override_amount: number | null
  parent_agent_name: string | null
  period_start: string
  period_end: string
  status: string
  created_at: string
}

interface Summary {
  total_pending: number
  total_approved: number
  total_paid: number
  total_all: number
}

interface Props extends PageProps {
  commissions: Commission[]
  summary: Summary
  date_range: DateRange
  approved_count: number
}

export default function CommissionsIndex({ commissions, summary, date_range, approved_count }: Props) {
  const { auth } = usePage<PageProps>().props
  const isAdmin = auth.user?.role === 'owner' || auth.user?.role === 'administrator'
  const statusColors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-700',
    approved: 'bg-blue-100 text-blue-700',
    paid: 'bg-green-100 text-green-700'
  }

  const formatCurrency = (amount: number) => `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

  const periodLabel = date_range.preset === 'all_time' ? '' : ` — ${getDateLabel(date_range)}`

  const columns: Column<Commission>[] = [
    {
      key: 'agent_name',
      header: 'Agent',
      sortable: true,
      render: (commission) => (
        <div>
          <div className="font-medium text-chestnut-charcoal">{commission.agent_name}</div>
          <div className="text-sm text-chestnut-charcoal/60">{commission.carrier_name}</div>
        </div>
      )
    },
    {
      key: 'policy_number',
      header: 'Policy',
      sortable: true,
      render: (commission) => <span className="text-chestnut-charcoal">#{commission.policy_number}</span>
    },
    {
      key: 'commission_type',
      header: 'Type',
      sortable: true,
      render: (commission) => (
        <div>
          <div className="capitalize">{commission.commission_type.replace('_', ' ')}</div>
          {commission.parent_agent_name && (
            <div className="text-xs text-chestnut-charcoal/60">From: {commission.parent_agent_name}</div>
          )}
        </div>
      )
    },
    {
      key: 'gross_amount',
      header: 'Gross',
      sortable: true,
      className: 'text-right font-medium',
      headerClassName: 'text-right',
      render: (commission) => formatCurrency(commission.gross_amount)
    },
    {
      key: 'agent_amount',
      header: 'Agent',
      sortable: true,
      className: 'text-right font-semibold',
      headerClassName: 'text-right',
      render: (commission) => formatCurrency(commission.agent_amount)
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (commission) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${statusColors[commission.status]}`}>
          {commission.status}
        </span>
      )
    },
    {
      key: 'actions',
      header: 'Actions',
      headerClassName: 'text-right',
      className: 'text-right',
      render: (commission) => (
        <div className="flex items-center justify-end gap-1">
          <Link
            href={`/commissions/${commission.id}`}
            className="p-2 text-chestnut-charcoal/60 hover:text-chestnut-forest"
          >
            <Eye className="h-4 w-4" />
          </Link>
          {isAdmin && commission.status === 'pending' && (
            <Link
              href={`/commissions/${commission.id}/approve`}
              method="post"
              as="button"
              className="p-2 text-green-600 hover:text-green-700"
            >
              <Check className="h-4 w-4" />
            </Link>
          )}
        </div>
      )
    }
  ]

  return (
    <AppLayout>
      <Head title="Commissions" />

      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">Commissions</h1>
            <p className="text-chestnut-charcoal/70 mt-1">Track and manage agent commissions</p>
          </div>
          <div className="flex items-center gap-3">
            <DateRangeFilter dateRange={date_range} path="/commissions" />
            {isAdmin && (
              <div className="flex gap-2">
                {(() => {
                  const params = new URLSearchParams()
                  if (date_range.preset) params.set('preset', date_range.preset)
                  if (date_range.preset === 'custom') {
                    if (date_range.start_date) params.set('start_date', date_range.start_date)
                    if (date_range.end_date) params.set('end_date', date_range.end_date)
                  }
                  const qs = params.toString()
                  const exportUrl = `/commissions/export_payout${qs ? `?${qs}` : ''}`
                  const disabled = approved_count === 0
                  const tooltip = 'Export approved commissions for bank ACH upload'
                  return disabled ? (
                    <button
                      type="button"
                      disabled
                      title={tooltip}
                      className="btn-secondary flex items-center gap-2 opacity-50 cursor-not-allowed"
                    >
                      <Download className="h-5 w-5" />
                      Export for Payout
                    </button>
                  ) : (
                    <a
                      href={exportUrl}
                      title={tooltip}
                      className="btn-secondary flex items-center gap-2"
                    >
                      <Download className="h-5 w-5" />
                      Export for Payout
                    </a>
                  )
                })()}
                <Link href="/commissions/import" className="btn-secondary">Import</Link>
                <Link href="/commissions/new" className="btn-primary flex items-center gap-2">
                  <Plus className="h-5 w-5" />
                  Add Commission
                </Link>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="card border-l-4 border-l-yellow-500">
            <h3 className="text-sm font-medium text-chestnut-charcoal/60 uppercase tracking-wide">Pending{periodLabel}</h3>
            <p className="text-2xl font-bold text-chestnut-charcoal mt-1">{formatCurrency(summary.total_pending)}</p>
          </div>
          <div className="card border-l-4 border-l-blue-500">
            <h3 className="text-sm font-medium text-chestnut-charcoal/60 uppercase tracking-wide">Approved{periodLabel}</h3>
            <p className="text-2xl font-bold text-chestnut-charcoal mt-1">{formatCurrency(summary.total_approved)}</p>
          </div>
          <div className="card border-l-4 border-l-green-500">
            <h3 className="text-sm font-medium text-chestnut-charcoal/60 uppercase tracking-wide">Paid{periodLabel}</h3>
            <p className="text-2xl font-bold text-chestnut-charcoal mt-1">{formatCurrency(summary.total_paid)}</p>
          </div>
          <div className="card border-l-4 border-l-chestnut-forest">
            <h3 className="text-sm font-medium text-chestnut-charcoal/60 uppercase tracking-wide">Total{periodLabel}</h3>
            <p className="text-2xl font-bold text-chestnut-charcoal mt-1">{formatCurrency(summary.total_all)}</p>
          </div>
        </div>

        <div className="card p-6">
          <DataTable
            data={commissions}
            columns={columns}
            searchPlaceholder="Search commissions..."
            searchKeys={['agent_name', 'policy_number', 'carrier_name']}
            pageSize={10}
            emptyMessage="No commissions recorded yet."
            getRowKey={(commission) => commission.id}
            onRowClick={(commission) => router.visit(`/commissions/${commission.id}`)}
          />
        </div>
      </div>
    </AppLayout>
  )
}
